import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import type { ModelSummary, Tag } from '@wml-crm/shared';

interface Props {
  autoFocus?: boolean;
  placeholder?: string;
}

export function SearchBar({ autoFocus, placeholder = 'Поиск модели по имени…' }: Props) {
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [suggestions, setSuggestions] = useState<ModelSummary[]>([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!q.trim() && !tag) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q);
      if (tag) params.set('tag', tag);
      params.set('limit', '8');
      api<ModelSummary[]>(`/api/models/search?${params.toString()}`)
        .then((data) => { if (!cancelled) { setSuggestions(data); setActive(0); } })
        .catch(() => { if (!cancelled) setSuggestions([]); });
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, tag]);

  return (
    <div className="search-wrap" ref={containerRef}>
      <span className="search-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
        </svg>
      </span>
      <input
        className="search-input"
        placeholder={placeholder}
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { setActive((i) => Math.min(i + 1, suggestions.length - 1)); e.preventDefault(); }
          else if (e.key === 'ArrowUp') { setActive((i) => Math.max(i - 1, 0)); e.preventDefault(); }
          else if (e.key === 'Enter' && suggestions[active]) { nav(`/model/${suggestions[active]!.id}`); setOpen(false); }
          else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {tags.length > 0 && (
        <select
          className="search-tag-filter"
          value={tag}
          onChange={(e) => { setTag(e.target.value); setOpen(true); }}
          title="Фильтр по тегу"
        >
          <option value="">Все теги</option>
          {tags.map((t) => (
            <option key={t.id} value={t.name}>{t.name}</option>
          ))}
        </select>
      )}
      {open && suggestions.length > 0 && (
        <div className="suggest-list">
          {suggestions.map((m, i) => (
            <div
              key={m.id}
              className={`suggest-item ${i === active ? 'active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); nav(`/model/${m.id}`); setOpen(false); }}
            >
              <span>{m.displayName}</span>
              <span className="meta">{m.accountCount} акк.</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
